/**
 * File validation utilities for consistent validation across the application
 */

// Constants
export const DEFAULT_MAX_FILE_SIZE_MB = 10
const VALID_PDF_MIME_TYPES = ['application/pdf'] as const
const PDF_FILE_EXTENSION = 'pdf'

// Error messages
const ERROR_NO_FILE_PROVIDED = 'No file provided'
const ERROR_INVALID_FILE_TYPE = (maxSizeMB: number) =>
  `Uploads can only be PDFs up to ${maxSizeMB} megabytes`

interface ValidationResult {
  isValid: boolean
  error: string | null
}

/**
 * Validates if a file is a PDF based on both MIME type and file extension
 * @param file - The file to validate
 * @param maxSizeMB - Maximum file size in megabytes (default: 10MB)
 * @returns Validation result with isValid flag and error message if invalid
 */
export const validatePDFFile = (
  file: File | null | undefined,
  maxSizeMB: number = DEFAULT_MAX_FILE_SIZE_MB,
): ValidationResult => {
  if (!file) {
    return { isValid: false, error: ERROR_NO_FILE_PROVIDED }
  }

  // Validate MIME type (can be spoofed but provides basic check)
  if (
    file.type &&
    !VALID_PDF_MIME_TYPES.includes(
      file.type as typeof VALID_PDF_MIME_TYPES[number],
    )
  ) {
    return {
      isValid: false,
      error: ERROR_INVALID_FILE_TYPE(maxSizeMB),
    }
  }

  // Validate file extension (more reliable than MIME type)
  const fileExtension = file.name.toLowerCase().split('.').pop()
  if (fileExtension !== PDF_FILE_EXTENSION) {
    return {
      isValid: false,
      error: ERROR_INVALID_FILE_TYPE(maxSizeMB),
    }
  }

  // Validate file size
  const maxSizeBytes = maxSizeMB * 1024 * 1024
  if (file.size > maxSizeBytes) {
    return {
      isValid: false,
      error: ERROR_INVALID_FILE_TYPE(maxSizeMB),
    }
  }

  return { isValid: true, error: null }
}

/**
 * Converts a file to base64 string
 * @param file - The file to convert
 * @returns Promise resolving to base64 encoded string (without data URL prefix)
 */
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      // Remove data URL prefix (e.g., "data:application/pdf;base64,")
      const result = reader.result as string
      const parts = result.split(',')
      const base64 = parts[1]
      if (!base64) {
        reject(new Error('Failed to convert file to base64'))
        return
      }
      resolve(base64)
    }
    reader.onerror = (error) => reject(error)
  })
}
